import type { ReactNode, Ref } from "react";
import { cn } from "@/lib/utils";
import { type ProvenanceInfo, ProvenanceMark } from "./ProvenanceMark.tsx";
import { SETTING_ROW_CLASS, settingId } from "./setting-anchors.ts";

/**
 * One settings row (openspec change settings-typed-controls, D1): the label and help on the
 * left, the control (or the value in words) where the value was, trailing from `@xl`; the
 * provenance line with the marks and the row's quiet actions (lock, reset); then what belongs
 * under the row (a confirmation, a refusal, an open editor). The row is the `#setting-<key>`
 * anchor a search pick or a summary link focuses.
 */
export function SettingRowFrame(props: {
  settingKey: string;
  /** The label's ID: every control names itself by it (`aria-labelledby`). */
  labelId: string;
  /** The provenance line's ID: controls are described by it. */
  sourceId: string;
  label: ReactNode;
  help?: ReactNode;
  /** More under the help (an MCP group's tools). */
  helpExtra?: ReactNode;
  control: ReactNode;
  /**
   * A switch stays beside its label at every width (its help then runs under both, full width,
   * until `@xl`); other controls move under the help.
   */
  inline?: boolean;
  /** The control takes the row's width under the help at every width (the egress lines). */
  wide?: boolean;
  marks: readonly ProvenanceInfo[];
  /** Said on the provenance line when no mark applies (the default). */
  unmarked?: ReactNode;
  /** Reasons, ranges and the row's actions, after the marks. */
  extra?: ReactNode;
  below?: ReactNode;
  controlRef?: Ref<HTMLSpanElement>;
}) {
  const extra = props.extra;
  const showLine = props.marks.length > 0 || props.unmarked || extra;
  const hasHelp = Boolean(props.help || props.helpExtra);
  const inline = props.inline && !props.wide;
  return (
    <li
      id={settingId(props.settingKey)}
      // A search pick or a summary link moves focus here (and marks the row for a moment).
      tabIndex={-1}
      className={SETTING_ROW_CLASS}
    >
      <div
        className={cn(
          "grid items-start gap-x-4 gap-y-0.5",
          inline ? "grid-cols-[minmax(0,1fr)_auto]" : "grid-cols-1",
          // From `@xl`: label and help on the left, the control trailing (at most half the row).
          !props.wide && "@xl:grid-cols-[minmax(0,1fr)_fit-content(50%)] @xl:gap-x-6",
        )}
      >
        <span id={props.labelId} className="text-sm font-medium text-fg @xl:col-start-1">
          {props.label}
        </span>
        {hasHelp ? (
          <div
            className={cn(
              "flex min-w-0 flex-col gap-0.5 @xl:col-span-1 @xl:col-start-1",
              inline && "col-span-2",
            )}
          >
            {props.help ? <span className="text-sm text-fg-secondary">{props.help}</span> : null}
            {props.helpExtra}
          </div>
        ) : null}
        <span
          ref={props.controlRef}
          data-setting-control=""
          className={cn(
            "flex items-center gap-2",
            inline ? "col-start-2 row-start-1 justify-end" : "mt-1.5 flex-wrap",
            !props.wide &&
              cn(
                "@xl:col-start-2 @xl:row-start-1 @xl:mt-0 @xl:justify-end",
                hasHelp && "@xl:row-span-2",
              ),
          )}
        >
          {props.control}
        </span>
      </div>
      {showLine ? (
        <p
          id={props.sourceId}
          className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted"
        >
          {props.marks.map((mark) => (
            <ProvenanceMark key={mark.kind} mark={mark} />
          ))}
          {props.marks.length === 0 && props.unmarked ? <span>{props.unmarked}</span> : null}
          {extra}
        </p>
      ) : null}
      {props.below}
    </li>
  );
}
